import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Sparkles,
  Download,
  Info,
  AlertTriangle,
  ShieldCheck,
  ArrowLeft,
  BarChart3,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { WizardPropuesta } from '../components/WizardPropuesta';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { ApiError } from '../lib/api';
import {
  propuestaApi,
  propuestaKeys,
  trazabilidadOk,
  descargarPropuestaMd,
  nombreArchivoPropuesta,
  fechaCortaGt,
  type GenealogiaAporte,
  type Propuesta,
  type PropuestaBloque,
  type PropuestaGenealogia,
  type PropuestaSeccion,
} from '../lib/propuesta';

/**
 * Propuesta v0 (/propuesta/:procesoId) — cierre del hilo M0. Toma los
 * artefactos cerrados del proceso y arma una propuesta trazable: cada bloque
 * cita sus aportes de origen (genealogía ⓘ, C2). Lo no respaldado se marca en
 * ámbar de aviso. Arriba, el índice de trazabilidad como badge (verde si ≥80).
 * Superficie de lectura sobria, tema claro para leer el documento.
 */
export function PropuestaPage() {
  const [wizardAbierto, setWizardAbierto] = useState(false);
  useSurfaceTheme('cabina', 'light');
  const { procesoId } = useParams<{ procesoId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);

  const propuesta = useQuery<Propuesta, ApiError>({
    queryKey: procesoId ? propuestaKeys.propuesta(procesoId) : ['propuesta', 'nada'],
    queryFn: () => propuestaApi.obtener(procesoId!),
    enabled: Boolean(procesoId),
    retry: false,
  });

  const generarMut = useMutation<Propuesta, ApiError>({
    mutationFn: () => propuestaApi.generar(procesoId!),
    onSuccess: (data) => {
      if (procesoId) qc.setQueryData(propuestaKeys.propuesta(procesoId), data);
    },
  });

  const data = generarMut.data ?? propuesta.data ?? null;
  const noHay = propuesta.isError && (propuesta.error as ApiError)?.status === 404;
  const yaExiste = Boolean(data);

  // Genealogía humana (quién/cuándo/qué) de TODA la propuesta en una sola query.
  // Si falla o aún carga, la UI cae a los ids cortos: nunca queda en blanco.
  const genealogia = useQuery<PropuestaGenealogia, ApiError>({
    queryKey: data ? propuestaKeys.genealogia(data.id) : ['genealogia', 'nada'],
    queryFn: () => propuestaApi.genealogia(data!.id),
    enabled: Boolean(data),
    retry: false,
  });
  const aportes = genealogia.data?.aportes ?? null;

  async function onDescargar() {
    if (!data) return;
    setErrorDescarga(null);
    setDescargando(true);
    try {
      await descargarPropuestaMd(data.id, nombreArchivoPropuesta(`proceso-${data.proceso_id}`));
    } catch (e) {
      setErrorDescarga((e as ApiError)?.detail ?? 'No pudimos descargar el Markdown.');
    } finally {
      setDescargando(false);
    }
  }

  const generarError = (generarMut.error as ApiError | null)?.detail ?? null;

  return (
    <div className="min-h-screen bg-surface-base">
      {/* Barra superior */}
      <header className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(procesoId ? `/cabina?proceso=${procesoId}` : '/cabina')}
            leadingIcon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
          >
            Cabina
          </Button>
          <span className="hidden items-center gap-2 font-display text-sm font-semibold text-content sm:flex">
            <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
            Propuesta del proyecto
          </span>
        </div>
        <div className="flex items-center gap-2">
          {procesoId && (
            <Link to={`/tablero/${procesoId}`}>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<BarChart3 className="h-4 w-4" aria-hidden="true" />}
              >
                Tablero
              </Button>
            </Link>
          )}
          {yaExiste && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onDescargar}
              loading={descargando}
              leadingIcon={<Download className="h-4 w-4" aria-hidden="true" />}
            >
              Descargar Markdown
            </Button>
          )}
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6">
        {/* Encabezado + índice de trazabilidad + acción generar */}
        <section className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-semibold text-content sm:text-3xl">
                Propuesta v0
              </h1>
              <p className="mt-1 font-sans text-sm text-content-muted">
                Del problema a una propuesta trazable: cada afirmación se sostiene
                en los aportes de la sala.
              </p>
            </div>
            {data && <IndiceTrazabilidad indice={data.indice_trazabilidad} version={data.version} />}
          </div>

          {wizardAbierto && procesoId && (
            <WizardPropuesta
              procesoId={procesoId}
              onListo={() => void qc.invalidateQueries({ queryKey: ['propuesta'] })}
              onCerrar={() => setWizardAbierto(false)}
            />
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              onClick={() => generarMut.mutate()}
              loading={generarMut.isPending}
              disabled={!procesoId}
              leadingIcon={<Sparkles className="h-5 w-5" aria-hidden="true" />}
            >
              {yaExiste ? 'Regenerar propuesta' : 'Generar propuesta'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setWizardAbierto((v) => !v)}
              disabled={!procesoId}
              leadingIcon={<Sparkles className="h-5 w-5" aria-hidden="true" />}
            >
              Redactar con IA
            </Button>
            {yaExiste && (
              <span className="font-sans text-xs text-content-muted">
                Se rearma desde los artefactos cerrados del proceso.
              </span>
            )}
            {generarError && (
              <span className="flex items-center gap-1.5 font-sans text-sm text-error">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {generarError}
              </span>
            )}
          </div>

          {errorDescarga && (
            <p className="flex items-center gap-1.5 font-sans text-sm text-error">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {errorDescarga}
            </p>
          )}
        </section>

        {/* Cuerpo */}
        {propuesta.isLoading ? (
          <p className="flex items-center gap-2 font-sans text-sm text-content-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Cargando la propuesta…
          </p>
        ) : data ? (
          <LeyendaEvidencia />
        ) : null}

        {data ? (
          <div className="flex flex-col gap-5">
            {[...data.contenido.secciones]
              .sort((a, b) => a.orden - b.orden)
              .map((seccion) => (
                <Seccion key={seccion.orden} seccion={seccion} aportes={aportes} />
              ))}
          </div>
        ) : noHay && !generarMut.isPending ? (
          <SinPropuesta />
        ) : null}
      </main>
    </div>
  );
}

/* ------------------------------- Piezas -------------------------------- */

function IndiceTrazabilidad({ indice, version }: { indice: number; version: number }) {
  const ok = trazabilidadOk(indice);
  const pct = Math.round(indice);
  return (
    <div className="flex flex-col items-end gap-1">
      <Badge variant={ok ? 'success' : 'warning'}>
        <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
        Trazabilidad {pct}%
      </Badge>
      <span className="font-sans text-xs text-content-muted">
        Versión {version} · {ok ? 'bien respaldada' : 'necesita más evidencia'}
      </span>
    </div>
  );
}

function LeyendaEvidencia() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-border bg-surface-raised px-4 py-3">
      <span className="flex items-center gap-1.5 font-sans text-xs text-content-muted">
        <Info className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        Genealogía: el bloque cita los aportes que lo respaldan.
      </span>
      <span className="flex items-center gap-1.5 font-sans text-xs text-content-muted">
        <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-hidden="true" />
        Resaltado ámbar: afirmación sin evidencia todavía.
      </span>
    </div>
  );
}

/** Mapa aporte_id → detalle humano; null mientras carga o si la query falló. */
type AportesMap = Record<string, GenealogiaAporte> | null;

function Seccion({ seccion, aportes }: { seccion: PropuestaSeccion; aportes: AportesMap }) {
  return (
    <Card title={`${seccion.orden}. ${seccion.titulo}`}>
      {seccion.bloques.length === 0 ? (
        <p className="font-sans text-sm italic text-content-muted">
          Esta sección todavía no tiene contenido generado.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {seccion.bloques.map((bloque, i) => (
            <Bloque key={i} bloque={bloque} aportes={aportes} />
          ))}
        </div>
      )}
    </Card>
  );
}

function Bloque({ bloque, aportes }: { bloque: PropuestaBloque; aportes: AportesMap }) {
  if (bloque.estructural) {
    // Intro/relleno de sección: encuadra la lista; no es una afirmación.
    return <p className="font-sans text-sm text-content-muted">{bloque.texto}</p>;
  }
  if (!bloque.respaldado) {
    // Sin evidencia: resaltado ámbar de aviso (no bloquea la lectura).
    return (
      <div className="rounded-md border border-recognition bg-recognition-soft px-3 py-2.5">
        <p className="font-sans text-sm text-content">{bloque.texto}</p>
        <p className="mt-1.5 flex items-center gap-1.5 font-sans text-xs text-[color:var(--color-recognition-text)]">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Sin respaldo en aportes. Revisá o sumá evidencia antes de decidir.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md px-3 py-2.5">
      <p className="font-sans text-sm text-content">{bloque.texto}</p>
      <Genealogia refs={bloque.refs} aportes={aportes} />
    </div>
  );
}

function Genealogia({ refs, aportes }: { refs: PropuestaBloque['refs']; aportes: AportesMap }) {
  const [abierto, setAbierto] = useState(false);
  if (refs.length === 0) return null;
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="inline-flex min-h-touch items-center gap-1.5 rounded-full border border-info px-2.5 py-1 font-sans text-xs font-semibold text-info transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
        Genealogía · {refs.length} {refs.length === 1 ? 'aporte' : 'aportes'}
      </button>
      {abierto && (
        <ul className="mt-2 flex flex-col gap-2 border-l-2 border-border pl-3">
          {refs.map((r, i) => {
            // El id corto sigue siendo la referencia dura: queda en el tooltip.
            const referencia = `Aporte ${corto(r.aporte_id)} · artefacto ${corto(r.artefacto_id)}`;
            const info = aportes?.[r.aporte_id];
            if (!info) {
              // Genealogía cargando o no disponible: se mantienen los ids cortos.
              return (
                <li
                  key={`${r.aporte_id}-${i}`}
                  className="font-sans text-xs text-content-muted"
                  title={referencia}
                >
                  <span className="tabular-nums">
                    Aporte <code className="text-content">{corto(r.aporte_id)}</code> · artefacto{' '}
                    <code className="text-content">{corto(r.artefacto_id)}</code>
                  </span>
                </li>
              );
            }
            return (
              <li
                key={`${r.aporte_id}-${i}`}
                className="font-sans text-xs text-content-muted"
                title={referencia}
              >
                {info.texto && <p className="text-content">«{breve(info.texto)}»</p>}
                <p className={info.texto ? 'mt-0.5' : undefined}>
                  {[
                    `${info.autor ?? 'Participante de sala'}${info.grupo ? ` (${info.grupo})` : ''}`,
                    fechaCortaGt(info.fecha),
                    info.herramienta,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Recorta un texto de aporte a una cita breve para la genealogía. */
function breve(texto: string, max = 140): string {
  const limpio = texto.trim();
  if (limpio.length <= max) return limpio;
  return `${limpio.slice(0, max).trimEnd()}…`;
}

/** Acorta un UUID a una forma citable sin exponer autoría (solo referencia). */
function corto(id: string): string {
  if (id.length <= 10) return id;
  return `${id.slice(0, 8)}…`;
}

function SinPropuesta() {
  return (
    <Card title="Todavía no hay una propuesta">
      <p className="font-sans text-sm text-content-muted">
        La propuesta v0 se arma a partir de los artefactos cerrados del proceso.
        Cerrá al menos una actividad en la Cabina y luego generá la propuesta:
        cada bloque quedará citando los aportes de la sala que lo respaldan.
      </p>
      <p className="mt-3 font-sans text-sm text-content-muted">
        Ejemplo: «El agua llega solo tres días por semana en la zona 3» pasa a ser
        un bloque del diagnóstico, citando quién lo aportó y en qué actividad.
      </p>
    </Card>
  );
}
